import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ComparisonActions } from '@comparison/data-access';
import { SnackTheme } from '@main/snackbar/const';
import { SnackbarFacade } from '@main/snackbar/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProductRating } from '@reviews/const';
import { ProductReviewsService } from '@reviews/data-access';
import { Category, Product, ProductScope, ProductService } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, timeout } from 'rxjs/operators';
import { ComparisonService } from '../services';

const MAX_CATEGORIES_COUNT = 10;
const MAX_PRODUCTS_IN_CATEGORY_COUNT = 10;

@Injectable()
export class ComparisonEffects {
  comparisonAddEntry$: Observable<
    | ComparisonActions.ComparisonAddEntrySuccess
    | ComparisonActions.ComparisonAddEntryError
    | ComparisonActions.ComparisonCategoriesLimitExceeded
    | ComparisonActions.ComparisonProductsLimitExceeded
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ComparisonActions.COMPARISON_ADD_ENTRY),
      mergeMap(({ payload: { productId } }: ComparisonActions.ComparisonAddEntry) =>
        combineLatest({
          product: this.productService.get(productId, ProductScope.DETAILS),
          rating: this.productReviewsService.getRating(productId),
          ratingIsLoaded: this.productReviewsService
            .getReviews(productId)
            .pipe(map(({ error, success, loading }) => !loading && (error || success))),
        }).pipe(
          filter(({ product, ratingIsLoaded }) => Boolean(!!product && ratingIsLoaded)),
          take(1),
          timeout(10000),
          switchMap(({ product, rating }) => {
            const category: Category = product.categories.slice(-1)[0];

            return this.isLimitExceeded(product.code, category.code).pipe(
              map((limitExceededAction) => {
                return { product, rating, limitExceededAction } as {
                  product: Product;
                  rating: ProductRating | null | undefined;
                  limitExceededAction:
                    | ComparisonActions.ComparisonCategoriesLimitExceeded
                    | ComparisonActions.ComparisonProductsLimitExceeded
                    | undefined;
                };
              }),
            );
          }),
          map(({ product, rating, limitExceededAction }) => {
            if (limitExceededAction) {
              return limitExceededAction;
            }

            return new ComparisonActions.ComparisonAddEntrySuccess({
              productId,
              entry: {
                category: product.categories.slice(-1)[0],
                productId: product.code,
                rating,
              },
            });
          }),
          catchError((e) => {
            console.error(e);

            return of(new ComparisonActions.ComparisonAddEntryError({ productId }));
          }),
        ),
      ),
    ),
  );

  comparisonCategoriesLimitExceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ComparisonActions.COMPARISON_CATEGORIES_LIMIT_EXCEEDED),
        tap(() => {
          this.snackbarFacade.setSnack({
            theme: SnackTheme.Yellow,
            title: 'Превышен лимит категорий, добавленных в Сравнение',
            html: `Максимальное число категорий&nbsp-&nbsp${MAX_CATEGORIES_COUNT}.
            </br><u><a href="${this.location.prepareExternalUrl('/comparison')}">Перейти в сравнение</a></u>`,
          });
        }),
      ),
    { dispatch: false },
  );

  comparisonProductsLimitExceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ComparisonActions.COMPARISON_PRODUCTS_LIMIT_EXCEEDED),
        tap(() => {
          this.snackbarFacade.setSnack({
            theme: SnackTheme.Yellow,
            title: 'Превышен лимит товаров в этой категории на странице Сравнения',
            html: `Максимальное число товаров категории&nbsp-&nbsp${MAX_PRODUCTS_IN_CATEGORY_COUNT}.
            </br><u><a href="${this.location.prepareExternalUrl('/comparison')}">Перейти в сравнение</a></u>`,
          });
        }),
      ),
    { dispatch: false },
  );

  comparisonAddEntrySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ComparisonActions.COMPARISON_ADD_ENTRY_SUCCESS),
        tap((action: ComparisonActions.ComparisonAddEntrySuccess) => {
          this.comparisonService.setProductAdded(action.payload.productId);
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly productService: ProductService,
    private readonly productReviewsService: ProductReviewsService,
    private readonly comparisonService: ComparisonService,
    private readonly snackbarFacade: SnackbarFacade,
    private readonly location: Location,
  ) {}

  isLimitExceeded(
    productId: string,
    categoryId: string,
  ): Observable<
    ComparisonActions.ComparisonCategoriesLimitExceeded | ComparisonActions.ComparisonProductsLimitExceeded | undefined
  > {
    return this.comparisonService.getSortedCategoriesWithCount().pipe(
      take(1),
      map((categories) => {
        const category = categories.find((item) => item.code === categoryId);

        if (categories.length >= MAX_CATEGORIES_COUNT && !category) {
          return new ComparisonActions.ComparisonCategoriesLimitExceeded({ productId, maxLimit: MAX_CATEGORIES_COUNT });
        }

        if (category?.count >= MAX_PRODUCTS_IN_CATEGORY_COUNT) {
          return new ComparisonActions.ComparisonProductsLimitExceeded({
            productId,
            maxLimit: MAX_PRODUCTS_IN_CATEGORY_COUNT,
          });
        }

        return undefined;
      }),
    );
  }
}
